import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Star, MessageSquare, ArrowLeft, Calendar, Wrench, Package, Video, Play, Heart, Eye, X, Settings, Pencil } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ListingCard from '../components/ListingCard';
import { TrustBadge, BadgeProgress } from '../components/TrustBadge';

import { API_URL } from '../utils/api';

// Video Card Component for the Videos tab
const VideoCard = ({ post, onPlay }) => {
  const thumbnailSrc = post.thumbnail_url?.startsWith('/api')
    ? `${API_URL}${post.thumbnail_url}`
    : post.thumbnail_url;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => onPlay(post)}
    >
      <div className="relative aspect-video bg-stone-900">
        {thumbnailSrc ? (
          <img 
            src={thumbnailSrc}
            alt={post.caption}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
            <Video className="h-12 w-12 text-stone-600" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="h-7 w-7 text-teal-600 ml-1" fill="currentColor" />
          </div>
        </div>
      </div>
      <div className="p-3">
        <p className="text-sm text-stone-700 line-clamp-2">{post.caption}</p>
        <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
          <span className="flex items-center gap-1">
            <Heart className="h-3.5 w-3.5" />
            {post.like_count || 0}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            {post.view_count || 0}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Video Player Modal
const VideoPlayerModal = ({ post, onClose }) => {
  const videoRef = useRef(null);
  
  const videoSrc = post.video_url?.startsWith('/api')
    ? `${API_URL}${post.video_url}`
    : post.video_url;

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            src={videoSrc}
            className="w-full h-full object-contain"
            controls
            autoPlay
          />
        </div>
        <div className="mt-4 text-white">
          <p className="text-lg">{post.caption}</p>
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {post.tags.map(tag => (
                <span key={tag} className="text-sm text-teal-400">#{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      <button
        className="absolute top-4 right-4 text-white hover:text-stone-300 p-2"
        onClick={onClose}
      >
        <X className="h-8 w-8" />
      </button>
    </div>
  );
};

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, api, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState('videos');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [creatorPosts, setCreatorPosts] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewType, setReviewType] = useState('tradie');

  useEffect(() => {
    fetchProfile();
    fetchServices();
    fetchCreatorPosts();
  }, [userId]);

  // Set default active tab based on what content exists
  useEffect(() => {
    if (profile && !loading) {
      if (creatorPosts.length > 0) {
        setActiveTab('videos');
      } else if (profile.listings?.length > 0) {
        setActiveTab('listings');
      } else if (profile.is_tradie && profile.trade_services?.length > 0) {
        setActiveTab('trades');
      } else {
        setActiveTab('about');
      }
    }
  }, [profile, creatorPosts, loading]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/users/${userId}/public`);
      setProfile(res.data);
    } catch (err) {
      console.error('Failed to fetch profile', err);
      toast.error('Profile not found');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/trades/services`);
      setServices(res.data.services);
    } catch (err) {
      console.error('Failed to fetch services', err);
    }
  };

  const fetchCreatorPosts = async () => {
    setLoadingVideos(true);
    try {
      const res = await axios.get(`${API_URL}/api/users/${userId}/creator-posts`);
      setCreatorPosts(res.data.posts || []);
    } catch (err) {
      console.error('Failed to fetch creator posts', err);
    } finally {
      setLoadingVideos(false);
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : serviceId;
  };

  const handleContact = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/profile/${userId}` } });
      return;
    }
    
    try {
      const res = await api().post('/conversations/direct', {
        participant_id: userId
      });
      navigate(`/messages/${res.data.id || res.data.conversation_id}`);
    } catch (err) {
      console.error('Failed to start conversation', err);
      toast.error('Failed to start conversation');
    }
  };

  const submitReview = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setSubmittingReview(true);
    try {
      if (reviewType === 'tradie') {
        await api().post(`/trades/${userId}/reviews?rating=${reviewRating}&comment=${encodeURIComponent(reviewComment)}`);
      } else {
        toast.error('Please leave seller reviews from a specific listing page');
        setSubmittingReview(false);
        return;
      }
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      fetchProfile();
    } catch (err) {
      console.error('Failed to submit review', err);
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const allPhotos = [...(profile.trade_photos || [])];
  const allReviews = [
    ...(profile.tradie_reviews || []).map(r => ({ ...r, type: 'tradie' })),
    ...(profile.seller_reviews || []).map(r => ({ ...r, type: 'seller' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Calculate tab counts
  const videosCount = creatorPosts.length;
  const listingsCount = profile.listings?.length || 0;
  const hasTradeServices = profile.is_tradie && profile.trade_services?.length > 0;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Back Button */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </div>

      {/* Profile Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Photo */}
            <div className="flex-shrink-0">
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-stone-100 overflow-hidden mx-auto md:mx-0 ring-4 ring-white shadow-lg">
                {profile.profile_photo ? (
                  <img
                    src={profile.profile_photo}
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-stone-400 bg-gradient-to-br from-stone-100 to-stone-200">
                    {profile.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2">{profile.name}</h1>
              
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-sm text-stone-600 mb-4">
                {profile.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {profile.trade_service_area && `${profile.trade_service_area}, `}
                    {profile.trade_service_state || profile.location}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Member since {new Date(profile.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
                </span>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-4">
                {profile.is_tradie && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-600 rounded-full text-sm font-medium">
                    <Wrench className="h-4 w-4" />
                    Tradie
                  </span>
                )}
                {profile.is_seller && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    <Package className="h-4 w-4" />
                    Seller
                  </span>
                )}
                {videosCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    <Video className="h-4 w-4" />
                    Creator
                  </span>
                )}
                {profile.accepts_custom_orders && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    Made to Order
                  </span>
                )}
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 mb-4 text-sm">
                {videosCount > 0 && (
                  <div className="text-center">
                    <div className="font-bold text-stone-900">{videosCount}</div>
                    <div className="text-stone-500">Videos</div>
                  </div>
                )}
                {listingsCount > 0 && (
                  <div className="text-center">
                    <div className="font-bold text-stone-900">{listingsCount}</div>
                    <div className="text-stone-500">Listings</div>
                  </div>
                )}
                {profile.tradie_rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold">{profile.tradie_rating}</span>
                    <span className="text-stone-500">({profile.tradie_review_count} reviews)</span>
                  </div>
                )}
                {profile.seller_rating && !profile.tradie_rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold">{profile.seller_rating}</span>
                    <span className="text-stone-500">({profile.seller_review_count} reviews)</span>
                  </div>
                )}
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                {profile.seller_badge?.tier && (
                  <TrustBadge tier={profile.seller_badge.tier} type="seller" size="medium" />
                )}
                {profile.tradie_badge?.tier && (
                  <TrustBadge tier={profile.tradie_badge.tier} type="tradie" size="medium" />
                )}
              </div>

              {/* Badge Progress (for own profile) */}
              {user?.id === userId && (profile.is_seller || profile.is_tradie) && !profile.seller_badge?.tier && (
                <div className="w-full max-w-md mx-auto md:mx-0">
                  <BadgeProgress 
                    fiveStarCount={profile.seller_five_star_count || 0} 
                    currentTier={profile.seller_badge?.tier}
                  />
                </div>
              )}

              {/* Actions */}
              {user?.id !== userId && (
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <Button
                    onClick={handleContact}
                    className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Message
                  </Button>
                  {profile.is_tradie && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setReviewType('tradie');
                        setShowReviewForm(true);
                      }}
                    >
                      <Star className="h-4 w-4 mr-2" />
                      Leave Review
                    </Button>
                  )}
                </div>
              )}

              {/* Own Profile Actions */}
              {user?.id === userId && (
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <Button
                    onClick={() => navigate('/profile')}
                    variant="outline"
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                  {videosCount > 0 && (
                    <Button
                      onClick={() => navigate('/creator/my-videos')}
                      variant="outline"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Videos
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {/* Videos Tab */}
            <button
              onClick={() => setActiveTab('videos')}
              className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === 'videos'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
              }`}
            >
              <Video className="h-4 w-4" />
              Videos
              {videosCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'videos' ? 'bg-teal-100 text-teal-600' : 'bg-stone-100 text-stone-600'
                }`}>
                  {videosCount}
                </span>
              )}
            </button>

            {/* Listings Tab */}
            <button
              onClick={() => setActiveTab('listings')}
              className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === 'listings'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
              }`}
            >
              <Package className="h-4 w-4" />
              Listings
              {listingsCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'listings' ? 'bg-teal-100 text-teal-600' : 'bg-stone-100 text-stone-600'
                }`}>
                  {listingsCount}
                </span>
              )}
            </button>

            {/* Trades Tab - Only show if they're a tradie */}
            {hasTradeServices && (
              <button
                onClick={() => setActiveTab('trades')}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'trades'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
                }`}
              >
                <Wrench className="h-4 w-4" />
                Trades
              </button>
            )}

            {/* About Tab */}
            <button
              onClick={() => setActiveTab('about')}
              className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === 'about'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
              }`}
            >
              About
            </button>

            {/* Portfolio Tab */}
            {allPhotos.length > 0 && (
              <button
                onClick={() => setActiveTab('portfolio')}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'portfolio'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
                }`}
              >
                Portfolio
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'portfolio' ? 'bg-teal-100 text-teal-600' : 'bg-stone-100 text-stone-600'
                }`}>
                  {allPhotos.length}
                </span>
              </button>
            )}

            {/* Reviews Tab */}
            {allReviews.length > 0 && (
              <button
                onClick={() => setActiveTab('reviews')}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'reviews'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
                }`}
              >
                <Star className="h-4 w-4" />
                Reviews
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'reviews' ? 'bg-teal-100 text-teal-600' : 'bg-stone-100 text-stone-600'
                }`}>
                  {allReviews.length}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            {loadingVideos ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full" />
              </div>
            ) : creatorPosts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {creatorPosts.map((post) => (
                  <VideoCard 
                    key={post.id} 
                    post={post} 
                    onPlay={setPlayingVideo}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg border border-stone-200">
                <Video className="mx-auto h-16 w-16 text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-700 mb-2">No videos yet</h3>
                <p className="text-stone-500">
                  {profile.name} hasn't posted any videos yet
                </p>
              </div>
            )}
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div>
            {listingsCount > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {profile.listings?.map((listing, index) => (
                  <ListingCard key={listing.id} listing={listing} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg border border-stone-200">
                <Package className="mx-auto h-16 w-16 text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-700 mb-2">No listings yet</h3>
                <p className="text-stone-500">
                  {profile.name} hasn't listed any items for sale yet
                </p>
              </div>
            )}
          </div>
        )}

        {/* Trades Tab */}
        {activeTab === 'trades' && hasTradeServices && (
          <div className="space-y-6">
            {/* Services */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Wrench className="h-5 w-5 text-teal-600" />
                Services Offered
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {profile.trade_services?.map((serviceId) => (
                  <div
                    key={serviceId}
                    className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-100"
                  >
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <Wrench className="h-4 w-4 text-teal-600" />
                    </div>
                    <span className="text-stone-700 font-medium">{getServiceName(serviceId)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Service Area */}
            {profile.trade_service_state && (
              <div className="bg-white border border-stone-200 rounded-lg p-6">
                <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-teal-600" />
                  Service Area
                </h3>
                <p className="text-stone-600">
                  {profile.trade_service_area && `${profile.trade_service_area}, `}
                  {profile.trade_service_state}
                </p>
              </div>
            )}

            {/* Trade Description */}
            {profile.trade_description && (
              <div className="bg-white border border-stone-200 rounded-lg p-6">
                <h3 className="font-semibold text-stone-900 mb-3">About My Trade Services</h3>
                <p className="text-stone-600 whitespace-pre-wrap">{profile.trade_description}</p>
              </div>
            )}

            {/* Contact CTA */}
            {user?.id !== userId && (
              <div className="bg-gradient-to-r from-teal-50 to-orange-50 rounded-lg p-6 border border-teal-100 text-center">
                <h3 className="font-semibold text-stone-900 mb-2">Need help with a project?</h3>
                <p className="text-stone-600 mb-4">Get in touch with {profile.name} to discuss your requirements</p>
                <Button
                  onClick={handleContact}
                  className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
              </div>
            )}
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="space-y-6">
            {(profile.bio || profile.trade_description) && (
              <div className="bg-white border border-stone-200 rounded-lg p-6">
                <h3 className="font-semibold text-stone-900 mb-3">About</h3>
                <p className="text-stone-600 whitespace-pre-wrap">
                  {profile.bio || profile.trade_description}
                </p>
              </div>
            )}
            
            {!profile.bio && !profile.trade_description && (
              <div className="bg-white border border-stone-200 rounded-lg p-6 text-center">
                <p className="text-stone-500">No bio added yet</p>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Quick Stats</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <Video className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  <div className="font-bold text-stone-900">{videosCount}</div>
                  <div className="text-xs text-stone-500">Videos</div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <Package className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  <div className="font-bold text-stone-900">{listingsCount}</div>
                  <div className="text-xs text-stone-500">Listings</div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <Star className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  <div className="font-bold text-stone-900">{allReviews.length}</div>
                  <div className="text-xs text-stone-500">Reviews</div>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-lg">
                  <Calendar className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  <div className="font-bold text-stone-900">
                    {new Date(profile.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
                  </div>
                  <div className="text-xs text-stone-500">Joined</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allPhotos.map((photo, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="aspect-square bg-stone-100 rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <img
                    src={photo}
                    alt=""
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {allReviews.length > 0 ? (
              allReviews.map((review) => (
                <div key={review.id} className="bg-white border border-stone-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-stone-100 overflow-hidden flex-shrink-0">
                      {review.reviewer_photo ? (
                        <img
                          src={review.reviewer_photo}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm font-bold text-stone-400">
                          {review.reviewer_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-stone-900">{review.reviewer_name}</span>
                        <span className="text-xs px-2 py-0.5 bg-stone-100 rounded-full text-stone-500">
                          {review.type === 'tradie' ? 'Trade Review' : 'Seller Review'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < review.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'fill-stone-200 text-stone-200'
                            }`}
                          />
                        ))}
                        <span className="text-sm text-stone-500 ml-2">
                          {new Date(review.created_at).toLocaleDateString('en-AU')}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="text-stone-600">{review.comment}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16 bg-white rounded-lg border border-stone-200">
                <Star className="mx-auto h-16 w-16 text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-700 mb-2">No reviews yet</h3>
                <p className="text-stone-500">
                  Be the first to leave a review for {profile.name}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <img
            src={selectedPhoto}
            alt=""
            className="max-w-full max-h-full object-contain"
          />
          <button
            className="absolute top-4 right-4 text-white hover:text-stone-300"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="h-8 w-8" />
          </button>
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <VideoPlayerModal 
          post={playingVideo} 
          onClose={() => setPlayingVideo(null)} 
        />
      )}

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full"
          >
            <h3 className="text-lg font-semibold mb-4">Leave a Review</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewRating(star)}
                    className="p-1"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        star <= reviewRating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'fill-stone-200 text-stone-200'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">Comment (optional)</label>
              <Textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Share your experience..."
                rows={4}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReviewForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={submitReview}
                disabled={submittingReview}
                className="flex-1 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;
